/** Class 9–10 discovery assessment — 34 Q, A–D matches Google Form / sheet column C:AJ. */

import hiPack from "./assessmentHi.json";
import { getRawForGrade } from "./gradePacks/index.js";

export const ASSESSMENT_INTRO = {
  kicker: 'Career DNA · Class 9–12',
  title: 'Let’s understand how you think.',
  body:
    'No marks. No right or wrong answers. Just a few honest choices and you’ll get a quick glimpse into: how you think, what comes naturally to you, and the kind of paths you might enjoy and do better.',
  bullets: [
    'How you think and solve things',
    'What feels natural to you',
    'Paths that might suit your style',
  ],
  cta: 'Show me how I think',
};

/** Intro + block bridges when UI is in Hindi (questions from `assessmentHi.json`; regen: `node frontend/scripts/emitAssessmentHiJson.mjs`). */
export const ASSESSMENT_INTRO_HI = {
  kicker: 'करियर डीएनए · कक्षा 9–12',
  title: 'आइए समझें कि आप कैसे सोचते हैं।',
  body:
    'नंबर का दबाव नहीं, सही–गलत का फंदा नहीं। बस कुछ ईमानदार चुनाव, और आपको एक झलक मिलेगी: आप कैसे सोचते हैं, क्या आपको स्वाभाविक रूप से आता है, और ऐसे रास्ते जिन्हें आप पसंद कर सकते हैं और जिनमें आप बेहतर कर सकते हैं।',
  bullets: [
    'आप कैसे सोचते और चीज़ें हल करते हैं',
    'क्या आपको स्वाभाविक लगता है',
    'आपकी शैली से मेल खाते रास्ते',
  ],
  cta: 'मेरी सोच दिखाएँ',
};

export function getAssessmentIntro(locale) {
  return String(locale || 'en').toLowerCase().startsWith('hi') ? ASSESSMENT_INTRO_HI : ASSESSMENT_INTRO;
}

/** Shown after completing each block (matches your script tone). */
export const LEVEL_TRANSITIONS = {
  1: "You are off to a great start—let us look at your working style.",
  2: "Good insight—now let us understand how you interact with others.",
  3: "Great—this gives a clear picture of your interaction style.",
  4: "Final section—this tells us what naturally drives you.",
};

export const LEVEL_TRANSITIONS_HI = {
  1: 'अच्छी शुरुआत — अब आपके काम करने का अंदाज़ समझते हैं।',
  2: 'अच्छी जानकारी — अब दूसरों के साथ बातचीत वाला हिस्सा।',
  3: 'बढ़िया — साथ मिलकर काम करने की तस्वीर साफ़ हो रही है।',
  4: 'आखिरी भाग — यह बताता है कि आपको क्या प्रेरित करता है।',
};

export function getLevelTransitionAfterQuestionId(locale, id) {
  const t = String(locale || 'en').toLowerCase().startsWith('hi') ? LEVEL_TRANSITIONS_HI : LEVEL_TRANSITIONS;
  if (id === 7) return t[1];
  if (id === 14) return t[2];
  if (id === 21) return t[3];
  if (id === 28) return t[4];
  return null;
}

/** True when Hindi pack still matches English Q1 (Hindi file not filled / out of sync). */
export function hiQuestionPackLooksUntranslated() {
  const base = getRawForGrade(10);
  const rows = hiPack?.questions;
  if (!Array.isArray(rows) || rows.length !== base.length) return true;
  return String(rows[0]?.text || '') === String(base[0]?.[0] || '');
}

/** Re-export grade-10 raw for tooling (`npm run gen:assessment-hi`). */
export { RAW } from "./gradePacks/10.js";

const LETTERS = ["A", "B", "C", "D"];

function mapRawToQuestions(raw) {
  return raw.map(([text, labels], i) => ({
    id: i + 1,
    level: i < 7 ? 1 : i < 14 ? 2 : i < 21 ? 3 : i < 28 ? 4 : 5,
    text,
    options: labels.map((label, j) => ({ letter: LETTERS[j], label })),
  }));
}

export const QUESTIONS = mapRawToQuestions(getRawForGrade(10));

let hiQuestionsCache = null;
let hiCacheKey = "";

/**
 * @param {string} locale `en` | `hi` (Hindi strings from `assessmentHi.json` when length matches base pack).
 * @param {number} [grade=10] Class 9–12; each grade has its own `gradePacks/{n}.js` RAW (same 34×A–D shape for scoring).
 */
export function getAssessmentQuestions(locale = "en", grade = 10) {
  const g = [9, 10, 11, 12].includes(Number(grade)) ? Number(grade) : 10;
  const baseRaw = getRawForGrade(g);
  const loc = String(locale || "en").toLowerCase();
  if (!loc.startsWith("hi")) return mapRawToQuestions(baseRaw);
  const key = `${loc}:${g}`;
  if (hiCacheKey === key && hiQuestionsCache) return hiQuestionsCache;
  const rows = hiPack?.questions;
  if (!Array.isArray(rows) || rows.length !== baseRaw.length) {
    return mapRawToQuestions(baseRaw);
  }
  const raw = rows.map((q) => [String(q.text || ""), Array.isArray(q.options) ? q.options.map(String) : []]);
  hiCacheKey = key;
  hiQuestionsCache = mapRawToQuestions(raw);
  return hiQuestionsCache;
}

/** @deprecated use getLevelTransitionAfterQuestionId(locale, id) */
export function levelTransitionAfterQuestionId(id) {
  return getLevelTransitionAfterQuestionId('en', id);
}
